/**
 * Site Group Settings V2 — "General" section: name, description,
 * virtual-group marker, billing/shipping addresses, and customer
 * reassignment.
 *
 * Contains:
 * - GeneralSection (default export)
 * - AddressesSubsection — billing/shipping addresses, folds edits
 *   into the synthetic `__pending_addresses` Formik field
 * - CustomerReassignSubsection — out-of-form customer reassignment
 * - RedirectDialog — post-reassignment "stay or go" choice dialog
 */

import CloudIcon from '@mui/icons-material/Cloud';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import { BillingShippingAddresses } from '@sophocles/components/BillingShippingAddresses';
import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import StatusChip from '@sophocles/components/StatusChip';
import { usePatchSiteGroupMutation } from '@sophocles/features/entities/siteGroups';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import {
  SettingsFieldRow,
  SettingsSection,
  SettingsSubsection,
  SettingsTextField,
} from '@sophocles/views/settings/components';

// Billing / shipping addresses subsection.  Renders the existing
// BillingShippingAddresses component (shared with the V1 customer
// + sitegroup details tabs) and folds user-initiated edits into
// the Formik `__pending_addresses` synthetic field so the unified
// Save bar activates.  Same idiom Sharing uses with
// `__pending_shares`.
function AddressesSubsection({ siteGroupId }) {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const handleChange = React.useCallback(
    payload => {
      setFieldValue('__pending_addresses', payload, false);
    },
    [setFieldValue],
  );
  return (
    <SettingsSubsection
      title={t('siteGroupSettings.v2.addresses', 'Addresses')}
      description={t(
        'siteGroupSettings.v2.addressesDescription',
        'Billing and shipping addresses for orders and invoices on this group.',
      )}
      bordered
    >
      <Box sx={{ py: 1.5 }}>
        <BillingShippingAddresses siteGroupId={siteGroupId} onChange={handleChange} variant="filled" />
      </Box>
    </SettingsSubsection>
  );
}

// Mirrors the inline `RedirectDialog` in legacy SiteGroupDetailsTab.js:322 —
// custom labels ("Stay on Current" / "Go to New") that WarningDialog
// doesn't expose, so we build it ourselves.
function RedirectDialog({ stayPath, newPath, onChoose }) {
  const { t } = useTranslation();
  const groupTerm = useSiteGroupTerm();
  return (
    <Dialog open onClose={() => onChoose(stayPath)}>
      <DialogTitle>Success!</DialogTitle>
      <DialogContent dividers>
        <Box>
          Do you want to stay in this Customer's {groupTerm.singular} list or go to the Customer that you just moved
          this {groupTerm.singular} to?
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={() => onChoose(stayPath)}>
          {t('settings.stayOnCurrent', 'Stay on Current')}
        </Button>
        <Button color="primary" onClick={() => onChoose(newPath)}>
          {t('settings.goToNew', 'Go to New')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Customer-reassignment surface.  Sits inside the General section
// but outside the Formik form — same UX legacy uses
// (`SiteGroupDetailsTab.js:213-238, 322-344`): toggle to reveal a
// CustomerAutocomplete, save calls `patchSiteGroup` directly, and a
// follow-up "stay or go to new customer" dialog runs after success.
//
// Visibility predicate is verbatim: staff or `view_other_customers`.
function CustomerReassignSubsection({ siteGroup, customer }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { isStaff, permissions } = useUser();
  const isStaffOrReseller = isStaff || permissions.isReseller;
  const [patchSiteGroup] = usePatchSiteGroupMutation();
  const [open, setOpen] = React.useState(false);
  const [target, setTarget] = React.useState(siteGroup.customer_id ?? customer?.id ?? null);
  const [redirectOpen, setRedirectOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const groupTerm = useSiteGroupTerm();

  if (!isStaffOrReseller) return null;

  const currentCustomer = customer?.id ?? params.cust_id ?? null;
  const isDifferent = target != null && target !== currentCustomer;

  const handleClose = () => {
    setOpen(false);
    setErrorMessage(null);
  };

  const handleSave = async () => {
    if (!isDifferent) {
      handleClose();
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const res = await patchSiteGroup({ id: siteGroup.id ?? siteGroup.site_group_id, body: { customer_id: target } });
      if (res?.error) {
        const data = res.error?.data;
        const detail =
          data?.errors?.[0]?.detail ||
          data?.errors?.[0]?.title ||
          data?.detail ||
          t('customer.reassignError', 'Reassignment failed.');
        setErrorMessage(detail);
        return;
      }
      setRedirectOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const staffChip = <StatusChip color="info" label={t('siteSettings.staffOnly', 'Staff Only')} />;

  return (
    <SettingsSubsection
      title={t('siteGroupSettings.v2.customerAssignment', 'Customer Assignment')}
      description={t(
        'siteGroupSettings.v2.customerAssignmentDescription',
        'Reassign this {{groupTerm}} to another customer. Saves immediately and offers to navigate.',
        { groupTerm: groupTerm.singular.toLowerCase() },
      )}
      chip={staffChip}
      bordered
    >
      {!open ? (
        <SettingsFieldRow
          label={t('siteGroupSettings.v2.currentCustomer', 'Current Customer')}
          description={customer?.name ?? '—'}
          controlWidth={180}
        >
          <Button startIcon={<SwapHorizIcon />} variant="outlined" onClick={() => setOpen(true)}>
            {t('customer.changeCustomer', 'Change Customer')}
          </Button>
        </SettingsFieldRow>
      ) : (
        <Stack spacing={2} sx={{ py: 1.5 }}>
          <CustomerAutocomplete
            fullWidth
            label={t('customer.changeCustomer', 'Change Customer')}
            value={target}
            onChange={(_e, customerId) => setTarget(customerId)}
            variant="filled"
          />
          <Alert severity="warning" sx={{ alignItems: 'center' }}>
            {t('customer.reassignCustomerMessage')}
          </Alert>
          {errorMessage ? (
            <Alert severity="error" onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={handleClose}>{t('actions.cancel', 'Cancel')}</Button>
            <Button variant="contained" disabled={!isDifferent || submitting} onClick={handleSave}>
              {t('actions.reassign', 'Reassign')}
            </Button>
          </Stack>
        </Stack>
      )}
      {redirectOpen ? (
        <RedirectDialog
          stayPath={`/c/${currentCustomer}/plants`}
          newPath={`/c/${target}/plants`}
          onChoose={path => {
            setRedirectOpen(false);
            navigate(path, { replace: true });
          }}
        />
      ) : null}
    </SettingsSubsection>
  );
}

export default function GeneralSection({ siteGroupId, siteGroup, customer, settings }) {
  const { t } = useTranslation();
  const groupTerm = useSiteGroupTerm();
  return (
    <SettingsSection
      id="settings-general"
      icon={<InfoOutlinedIcon />}
      title={t('siteGroupSettings.v2.general', 'General')}
      description={t(
        'siteGroupSettings.v2.generalDescription',
        '{{groupTerm}} name, description, and customer assignment.',
        { groupTerm: groupTerm.singular.toLowerCase() },
      )}
    >
      <SettingsSubsection title={t('siteGroupSettings.v2.identity', 'Identity')} bordered>
        <SettingsFieldRow
          label={t('general.name', 'Name')}
          description={t('siteGroupSettings.v2.nameDescription', "The {{groupTerm}}'s display name.", {
            groupTerm: groupTerm.singular.toLowerCase(),
          })}
          controlWidth={320}
          required
        >
          <SettingsTextField name="general.name" hiddenLabel fullWidth />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('general.description', 'Description')}
          description={t('siteGroupSettings.v2.descriptionDescription', 'Short summary about this group.')}
          controlWidth={400}
        >
          <SettingsTextField name="general.description" hiddenLabel fullWidth multiline minRows={2} />
        </SettingsFieldRow>
        {settings.general?.is_virtual ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', px: 2, pb: 2 }}>
            <CloudIcon fontSize="small" />
            <Typography variant="caption">{t('siteGroups.isVirtualMessage')}</Typography>
          </Box>
        ) : null}
      </SettingsSubsection>

      {!(__EXCLUDE_SALES__ && __EXCLUDE_RETURNS__) && <AddressesSubsection siteGroupId={siteGroupId} />}

      <CustomerReassignSubsection siteGroup={siteGroup} customer={customer} />
    </SettingsSection>
  );
}
